/**
 * Metric display logic - parsing, formatting, and displaying metric values
 */

import { MetricType, MetricDisplay, ParsedNumberWithUnit } from './types';
import {
    formatEnumLabel,
    formatPropertyValue,
    formatNumber,
    formatRounded,
    isEnumString
} from './formatting.utils';

const DEFAULT_UNITS: Record<MetricType, string> = {
    distance: 'm',
    time: 's',
    steps: 'steps',
    speed: 'km/h',
    pace: '/km'
};

/**
 * Splits a string value into numeric value and unit
 * @example splitValueUnit('100 km') // { value: '100', unit: 'km' }
 */
const splitValueUnit = (value: string): MetricDisplay => {
    const parts = value.trim().split(' ');
    if (parts.length < 2) {
        return { value, unit: undefined };
    }

    const unitCandidate = parts[parts.length - 1];
    const valueCandidate = parts.slice(0, -1).join(' ');

    if (/[a-zA-Z/%]/.test(unitCandidate)) {
        return { value: valueCandidate, unit: unitCandidate };
    }

    return { value, unit: undefined };
};

/**
 * Parses a string into a number with optional unit
 * @example parseNumberWithUnit('42.5 km') // { value: 42.5, unit: 'km' }
 */
const parseNumberWithUnit = (raw: string): ParsedNumberWithUnit | null => {
    const cleaned = raw.trim().replace(/,/g, '');
    const match = cleaned.match(/^(-?\d+(?:\.\d+)?)(?:\s*([a-zA-Z/]+))?$/);
    if (!match) {
        return null;
    }
    const [, numberValue, unitValue] = match;
    const parsed = Number(numberValue);
    if (Number.isNaN(parsed)) {
        return null;
    }

    return {
        value: parsed,
        unit: unitValue ? unitValue.toLowerCase() : undefined
    };
};

/**
 * Determines the metric type from a property key
 */
const getMetricType = (normalizedKey: string): MetricType | null => {
    if (
        normalizedKey.includes('timezone') ||
        normalizedKey.includes('timestamp')
    ) {
        return null;
    }
    if (normalizedKey.includes('pace')) return 'pace';
    if (normalizedKey.includes('step')) return 'steps';
    if (normalizedKey.includes('distance')) return 'distance';
    if (normalizedKey.includes('duration') || normalizedKey.includes('time'))
        return 'time';
    if (normalizedKey.includes('speed')) return 'speed';
    return null;
};

/**
 * Checks if a metric value is invalid (negative or not finite)
 */
const isInvalidMetricValue = (value: number): boolean =>
    !Number.isFinite(value) || value < 0;

/**
 * Formats a metric value based on its type
 */
const formatMetricValue = (metricType: MetricType, value: number): string => {
    if (metricType === 'speed') {
        return formatNumber(value, 1);
    }

    return formatRounded(value, 3);
};

/**
 * Formats a duration in seconds as HH:MM:SS, MM:SS (< 1h), or SS s (< 1min)
 */
const formatDuration = (totalSeconds: number): MetricDisplay => {
    const seconds = Math.round(totalSeconds);
    if (seconds < 60) {
        return { value: String(seconds), unit: 's' };
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return hours > 0
        ? {
              value: `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`,
              unit: undefined
          }
        : {
              value: `${pad(minutes)}:${pad(remainingSeconds)}`,
              unit: undefined
          };
};

/**
 * Formats a pace in seconds per kilometre as MM:SS, without wrapping past an hour
 */
const formatPace = (secondsPerKm: number): MetricDisplay => {
    if (!Number.isFinite(secondsPerKm) || secondsPerKm <= 0) {
        return { value: '—', unit: undefined };
    }

    const seconds = Math.round(secondsPerKm);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return {
        value: `${minutes}:${String(remainingSeconds).padStart(2, '0')}`,
        unit: '/km'
    };
};

/**
 * Formats a distance in meters, converting to km when >= 1km
 */
const formatDistance = (meters: number): MetricDisplay =>
    meters >= 1000
        ? { value: formatNumber(meters / 1000, 2), unit: 'km' }
        : { value: formatNumber(meters, 0), unit: 'm' };

/**
 * Gets the display value and unit for a metric
 * Handles various input types and formats appropriately
 */
export const getMetricDisplay = (
    key: string,
    rawValue: unknown
): MetricDisplay => {
    if (rawValue === null || rawValue === undefined) {
        return { value: '—', unit: undefined };
    }

    const normalized = key.toLowerCase();
    const metricType = getMetricType(normalized);

    // Handle string values
    if (typeof rawValue === 'string') {
        if (isEnumString(rawValue)) {
            return { value: formatEnumLabel(rawValue), unit: undefined };
        }

        const parsed = parseNumberWithUnit(rawValue);
        if (parsed && metricType) {
            if (['distance', 'time', 'steps'].includes(metricType)) {
                if (isInvalidMetricValue(parsed.value)) {
                    return { value: '—', unit: undefined };
                }
            }

            if (metricType === 'time') {
                return formatDuration(parsed.value);
            }
            if (metricType === 'distance') {
                return formatDistance(parsed.value);
            }
            if (metricType === 'pace') {
                return formatPace(parsed.value);
            }

            return {
                value: formatMetricValue(metricType, parsed.value),
                unit: parsed.unit ?? DEFAULT_UNITS[metricType]
            };
        }

        return splitValueUnit(rawValue);
    }

    // Handle numeric values
    if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
        if (metricType && ['distance', 'time', 'steps'].includes(metricType)) {
            if (isInvalidMetricValue(rawValue)) {
                return { value: '—', unit: undefined };
            }
        }

        if (metricType === 'time') {
            return formatDuration(rawValue);
        }
        if (metricType === 'distance') {
            return formatDistance(rawValue);
        }
        if (metricType === 'pace') {
            return formatPace(rawValue);
        }

        if (metricType) {
            return {
                value: formatMetricValue(metricType, rawValue),
                unit: DEFAULT_UNITS[metricType]
            };
        }

        // Domain-specific formatting
        if (normalized.includes('calorie') || normalized.includes('burned')) {
            return { value: formatNumber(rawValue, 0), unit: 'kcal' };
        }
        if (normalized.includes('points')) {
            return { value: formatNumber(rawValue, 0), unit: 'pts' };
        }
        if (normalized.includes('elevation')) {
            return { value: formatNumber(rawValue, 0), unit: 'm' };
        }
        if (normalized.includes('heart')) {
            return { value: formatNumber(rawValue, 0), unit: 'bpm' };
        }

        return { value: formatNumber(rawValue, 2), unit: undefined };
    }

    return { value: formatPropertyValue(rawValue), unit: undefined };
};
